import React, { useCallback, useContext, useEffect, useState } from 'react';
import RecapFormalityShow from '@europrocurement/l2d-modules/modules/ParcoursFormalite/components/RecapFormalityShow';
import { Box, Container } from '@mui/system';
import {
    CollectionDeDossierCollectiondossierRead,
    CollectionDeDossierJsonldCollectiondossierRead,
    FichiersDossierJsonldDocumentfileRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { MESSAGE_FROM_PRESCRIBER, useFormaliteService } from '@europrocurement/l2d-domain';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { Button, CircularProgress, Typography } from '@mui/material';
import { useFullLayout } from '@europrocurement/flexy-components/components/templates/FullLayout/FullLayoutContext';
import { useModal } from '@europrocurement/flexy-components';
import { useParcoursFormalite, useParcoursFormalitreHeader } from '../provider';
import { parcoursFormalitePathResolver } from '../parcoursFormalitePathResolver';
import { loadDossier } from '../functions/docFunctions';
import { DossierPrescriberContext } from '../../Dossiers/providers';

const StepRecap: React.FunctionComponent = function () {
    const snackbar = useSnackbar();

    const {
        getCurrentDossier,
        getCurrentCollection,
        getPrescripteur,
        currentSousClient,
        setLoading,
    } = useParcoursFormalite();

    React.useEffect(() => {
        setLoading(false);
    }, [setLoading]);

    const { fichiersDossierApi, dossierApi, collectionApi, messageApi } = useFormaliteService();
    const { sousClients, prescriberLoaded, setPrescriberId } = useContext(DossierPrescriberContext);
    const kc = UseKeycloakService();

    const [collection, setCollection] =
        useState<CollectionDeDossierJsonldCollectiondossierRead | null>(getCurrentCollection());

    const dossier = getCurrentDossier();
    const navigate = useNavigate();
    const prescripteur = getPrescripteur();
    const sousClient = currentSousClient;

    const [currentDocs, setCurrentDocs] =
        React.useState<Array<FichiersDossierJsonldDocumentfileRead> | null>(null);

    useEffect(() => {
        setPrescriberId(prescripteur.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prescripteur]);

    const reloadDossierAndDocs = useCallback(async () => {
        if (dossier) {
            const prom = loadDossier(dossier, fichiersDossierApi);
            return await prom.then((res) => {
                const fichiers = res.data['hydra:member'];
                setCurrentDocs(fichiers);
            });
        }
        return Promise.resolve();
    }, [dossier, fichiersDossierApi]);

    useEffect(() => {
        if (dossier && dossier.id && !currentDocs) {
            reloadDossierAndDocs();
        }
    }, [currentDocs, dossier, fichiersDossierApi, reloadDossierAndDocs]);

    const { changeParcoursSteps, setIsParcoursPath } = useParcoursFormalitreHeader();
    const { validate } = useParcoursFormalite();
    const { setProfileHeaderVisible } = useFullLayout();
    React.useEffect(() => {
        changeParcoursSteps(4);
        setProfileHeaderVisible(false);
        setIsParcoursPath(true);
        return () => {
            setIsParcoursPath(false);
            setProfileHeaderVisible(true);
        };
    }, [setProfileHeaderVisible, changeParcoursSteps, setIsParcoursPath]);

    const modals = useModal();

    const onChangeHandler = useCallback(
        async (
            innerCollection: CollectionDeDossierCollectiondossierRead,
            refInterne: string | null,
            commentaires: string | null,
            dossierUrgent: boolean | null,
            typeFacturation: number,
            clientId: number,
        ) => {
            setLoading(true);
            if (innerCollection.dossiers?.length !== 1) {
                throw new Error('error in dossier length');
            }
            const innerDossier = innerCollection.dossiers[0];

            const freshDossier = (await dossierApi.apiDossiersIdGet({ id: `${innerDossier.id}` }))
                .data;

            await dossierApi.apiDossiersIdPut({
                id: `${innerDossier.id}`,
                dossierJsonldDossierUpdate: {
                    societeDenomination: freshDossier.societeDenomination,
                    jsonObject: freshDossier.jsonObject,
                    societeSiren: freshDossier.societeSiren,
                    acompteAttendu: freshDossier.acompteAttendu,
                    affaire: freshDossier.affaire,
                    collection: freshDossier.collection,
                    societeRef: refInterne,
                },
            });

            if (!collection || !collection.id) {
                throw new Error('error in Collection');
            }

            const freshCollection = (
                await collectionApi.apiCollectionsDossiersIdGet({ id: `${collection.id}` })
            ).data;

            if (!freshCollection || !freshCollection.id) {
                throw new Error('missing freshCollection');
            }

            if (!freshCollection.client) {
                throw new Error('missing client');
            }
            if (!freshCollection.prescripteur) {
                throw new Error('missing prescripteur');
            }

            await collectionApi
                .apiCollectionsDossiersIdPatch({
                    id: `${freshCollection?.id}`,
                    collectionDeDossierCollectiondossierUpdate: {
                        prescripteur: freshCollection.prescripteur?.replace(
                            '/tiers/tiers',
                            '/formalite/tiers',
                        ),
                        urgent: dossierUrgent === true,
                        client: `/formalite/tiers/${clientId}`,
                        infoComp: freshCollection.infoComp,
                        libelle: freshCollection.libelle,
                    },
                })
                .then((response) => {
                    setCollection(response.data);
                });

            if (commentaires && commentaires !== '' && innerDossier.id) {
                await messageApi
                    .apiDossiersDossierMessagesGetCollection({
                        dossier: innerDossier.id as unknown as string,
                    })
                    .then((response) => {
                        response.data['hydra:member'].forEach((message) => {
                            if (message.id !== undefined) {
                                messageApi.apiMessagesIdDelete({
                                    id: message.id as unknown as string,
                                });
                            }
                        });
                    });

                await messageApi.apiMessagesPost({
                    messageJsonldMessageCreate: {
                        expediteur: MESSAGE_FROM_PRESCRIBER,
                        expediteurNom: kc.getName(),
                        message: commentaires,
                        dossier: `/formalite/dossiers/${innerDossier.id}`,
                    },
                });
            }
            setLoading(false);
        },
        [setLoading, dossierApi, collection, collectionApi, messageApi, kc],
    );

    const onSubmitHandler = useCallback(
        async (innerCollection: CollectionDeDossierCollectiondossierRead) => {
            setLoading(true);
            if (innerCollection.dossiers?.length !== 1) {
                throw new Error('error in dossier length');
            }

            const res = await validate(collection);

            setTimeout(() => {
                setLoading(false);

                snackbar.enqueueSnackbar('Dossier envoyé !');
                if (res.data.dossiers) {
                    if (res.data.dossiers.length > 1) {
                        modals.modalActions.call(
                            <Box
                                sx={{
                                    maxWidth: 'sm',
                                    flexDirection: 'column',
                                    gap: 2,
                                    display: 'flex',
                                }}
                            >
                                <Typography
                                    variant="l2dh6"
                                    fontWeight={600}
                                >
                                    Plusieurs dossiers ont été créés pour traiter correctement votre
                                    commande.
                                </Typography>
                                <Typography>
                                    Vous pouvez les retrouver dans la liste des dossiers ou naviguer
                                    dans les différents dossiers de la commande en bas de la fiche
                                    dossier.
                                </Typography>
                                <Button
                                    onClick={() => {
                                        navigate(
                                            `/dossiers${res.data.dossiers ? `/${res.data.dossiers[0].id}` : ''}`,
                                        );
                                        modals.modalActions.reset();
                                    }}
                                >
                                    Voir le premier dossier
                                </Button>
                            </Box>,
                            false,
                            false,
                            () => {
                                navigate(
                                    `/dossiers${res.data.dossiers ? `/${res.data.dossiers[0].id}` : ''}`,
                                );
                            },
                        );
                    } else {
                        navigate(`/dossiers/${res.data.dossiers[0].id}`);
                    }
                }
            }, 1500);
        },
        [setLoading, collection, snackbar, navigate, modals.modalActions, validate],
    );

    if (!collection || !currentDocs) {
        return null;
    }
    return (
        <Container>
            {prescriberLoaded ? (
                <RecapFormalityShow
                    onPrevious={() => {
                        navigate(parcoursFormalitePathResolver('stepDocs'));
                    }}
                    sousClients={sousClients}
                    collection={collection}
                    fichiers={currentDocs}
                    prescripteur={prescripteur}
                    sousClient={sousClient}
                    onChange={onChangeHandler}
                    onSubmit={onSubmitHandler}
                    commentairesDossier=""
                />
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                    <CircularProgress />
                </Box>
            )}
        </Container>
    );
};

export default StepRecap;
